import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flame, RefreshCw, MapPin, Bike, Car, AlertTriangle, Users as UsersIcon } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { publicSettings } from '../services/publicSettings';
import { loadGoogleMaps } from '../services/googleMapsLoader';
import { toast } from 'sonner';

// City → default map centres. Kept in sync with ShipitNow's geofence (Ottawa
// downtown + Ibadan Ring Road).
const CITY_CENTERS = {
  Ottawa: { lat: 45.4215, lng: -75.6972 },
  Ibadan: { lat: 7.3775, lng: 3.9470 },
};

const COVERAGE_STYLES = {
  empty: { label: 'No riders', bar: 'bg-rose-500', text: 'text-rose-700', chip: 'bg-rose-50 text-rose-700 border-rose-100' },
  low: { label: 'Low', bar: 'bg-amber-500', text: 'text-amber-700', chip: 'bg-amber-50 text-amber-700 border-amber-100' },
  medium: { label: 'Medium', bar: 'bg-sky-500', text: 'text-sky-700', chip: 'bg-sky-50 text-sky-700 border-sky-100' },
  high: { label: 'Healthy', bar: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
};

const HeatmapPage = () => {
  const [city, setCity] = useState('Ottawa');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/heatmap/coverage', { params: { city } });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load heatmap'); }
    finally { setLoading(false); }
  };

  // Reload when the city tab changes.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city]);

  // Poll every 20 s so admins see coverage change as drivers go on/offline
  // without needing to click Refresh.
  useEffect(() => {
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [city]);

  const zones = data?.zones || [];
  const totals = data?.totals || { drivers_online: 0, couriers_online: 0, empty_zones: 0, unassigned_orders: 0 };

  return (
    <div className="pb-16">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Flame className="h-6 w-6 text-orange-500" />
            Coverage heatmap
          </h1>
          <p className="text-slate-500 mt-1">
            Live density of online drivers and couriers per delivery zone. Empty zones flagged in red so ops can broadcast fast.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5" data-testid="heatmap-city-tabs">
            {['Ottawa', 'Ibadan'].map((c) => (
              <button
                key={c}
                onClick={() => setCity(c)}
                data-testid={`heatmap-city-${c.toLowerCase()}`}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition ${city === c ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                {c}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="heatmap-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {/* Summary counters */}
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="stat-drivers-online" icon={Car} label="Drivers online" value={totals.drivers_online} tone="indigo" />
        <StatCard testid="stat-couriers-online" icon={Bike} label="Couriers online" value={totals.couriers_online} tone="sky" />
        <StatCard testid="stat-empty-zones" icon={AlertTriangle} label="Zones with 0 riders" value={totals.empty_zones} tone={totals.empty_zones ? 'rose' : 'emerald'} />
        <StatCard testid="stat-unassigned-orders" icon={UsersIcon} label="Unassigned orders" value={totals.unassigned_orders} tone={totals.unassigned_orders ? 'amber' : 'emerald'} />
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Map */}
        <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="heatmap-map-container">
          <HeatmapMap city={city} zones={zones} selectedId={selected?.id} onSelect={setSelected} />
        </div>

        {/* Zone breakdown */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">Zone breakdown</div>
              <div className="text-[11px] text-slate-500">Sorted worst → best</div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100" data-testid="heatmap-zone-list">
              {zones.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">No zones found for {city}.</div>
              ) : zones.map((z) => {
                const style = COVERAGE_STYLES[z.coverage] || COVERAGE_STYLES.empty;
                const total = z.drivers_online + z.couriers_online;
                const width = Math.min(100, Math.max(4, total * 12));
                const isSel = selected?.id === z.id;
                return (
                  <button
                    type="button"
                    key={z.id}
                    onClick={() => setSelected(z)}
                    data-testid={`heatmap-zone-row-${z.id}`}
                    className={`w-full text-left px-4 py-3 transition hover:bg-slate-50 ${isSel ? 'bg-slate-50' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: z.color }} />
                        <span className="font-medium text-slate-900 truncate">{z.name}</span>
                        <Badge variant="outline" className={`text-[10px] font-semibold uppercase tracking-wider ${style.chip}`}>{style.label}</Badge>
                      </div>
                      <div className="text-[11px] text-slate-500 shrink-0">{z.city}</div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1"><Car className="h-3 w-3 text-indigo-500" /> {z.drivers_online}</span>
                      <span className="inline-flex items-center gap-1"><Bike className="h-3 w-3 text-sky-500" /> {z.couriers_online}</span>
                      {z.unassigned_orders > 0 && (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" title="Unassigned orders">
                          <AlertTriangle className="h-3 w-3" /> {z.unassigned_orders} orders waiting
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${style.bar}`} style={{ width: `${width}%` }} />
                    </div>
                  </button>
                );
              })}
              {data?.unzoned && (data.unzoned.drivers_online + data.unzoned.couriers_online > 0) && (
                <div className="px-4 py-3 bg-slate-50" data-testid="heatmap-unzoned">
                  <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" /> Unzoned riders online
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1"><Car className="h-3 w-3 text-indigo-500" /> {data.unzoned.drivers_online} drivers</span>
                    <span className="inline-flex items-center gap-1"><Bike className="h-3 w-3 text-sky-500" /> {data.unzoned.couriers_online} couriers</span>
                    <span className="text-slate-400">— haven&apos;t picked any zone yet</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {selected && (
            <SelectedZoneCard zone={selected} onClose={() => setSelected(null)} />
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone = 'slate', testid }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    sky: 'bg-sky-50 text-sky-700 border-sky-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-80">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 text-3xl font-bold tabular-nums">{value}</div>
    </div>
  );
};

const SelectedZoneCard = ({ zone, onClose }) => (
  <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`heatmap-selected-${zone.id}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: zone.color }} />
        <div className="font-semibold text-slate-900">{zone.name}</div>
        <Badge variant="outline" className="text-[10px]">{zone.city}</Badge>
      </div>
      <button type="button" onClick={onClose} className="text-xs text-slate-500 hover:text-slate-800">Close</button>
    </div>
    <div className="mt-2 text-xs text-slate-500">
      Bounding box: {zone.bbox?.min_lat?.toFixed?.(3)}, {zone.bbox?.min_lng?.toFixed?.(3)} → {zone.bbox?.max_lat?.toFixed?.(3)}, {zone.bbox?.max_lng?.toFixed?.(3)}
    </div>
    {zone.sample_riders?.length > 0 ? (
      <div className="mt-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Riders online here</div>
        <ul className="space-y-1.5">
          {zone.sample_riders.map((r) => (
            <li key={r.driver_id} className="flex items-center gap-2 text-sm">
              {r.kind === 'courier' ? <Bike className="h-3.5 w-3.5 text-sky-500" /> : <Car className="h-3.5 w-3.5 text-indigo-500" />}
              <span className="text-slate-800">{r.name}</span>
              <span className="text-slate-400 text-xs">· {r.vehicle_type}</span>
              {r.is_busy && <Badge className="bg-amber-500 hover:bg-amber-500 text-white border-0 text-[9px] py-0 px-1.5">busy</Badge>}
            </li>
          ))}
        </ul>
      </div>
    ) : (
      <div className="mt-3 text-xs text-rose-600 font-medium">No riders online in this zone right now.</div>
    )}
  </div>
);

// Renders zone rectangles on a Google Map when the API key is set, otherwise
// a lightweight CSS grid preview so the page still communicates coverage.
const HeatmapMap = ({ city, zones, selectedId, onSelect }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const rectsRef = useRef({});
  const markersRef = useRef({});
  const [provider, setProvider] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    publicSettings.get().then(async (s) => {
      const key = s?.google_maps_api_key;
      if (!key) { if (!cancelled) setProvider('fallback'); return; }
      const g = await loadGoogleMaps(key).catch(() => null);
      if (cancelled || !g) { setProvider('fallback'); return; }
      mapRef.current = new g.maps.Map(containerRef.current, {
        center: CITY_CENTERS[city] || CITY_CENTERS.Ottawa,
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
      });
      setProvider('google');
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // Recenter when city changes
  useEffect(() => {
    if (provider === 'google' && mapRef.current) {
      mapRef.current.panTo(CITY_CENTERS[city] || CITY_CENTERS.Ottawa);
    }
  }, [city, provider]);

  // Draw / update rectangles when zones change. Fill opacity encodes density.
  useEffect(() => {
    if (provider !== 'google' || !mapRef.current) return;
    const g = window.google;
    const activeIds = new Set(zones.map((z) => z.id));

    // Cleanup rects no longer present
    Object.entries(rectsRef.current).forEach(([id, rect]) => {
      if (!activeIds.has(id)) { rect.setMap(null); delete rectsRef.current[id]; }
    });

    zones.forEach((z) => {
      if (!z.bbox?.min_lat) return;
      const bounds = { south: z.bbox.min_lat, west: z.bbox.min_lng, north: z.bbox.max_lat, east: z.bbox.max_lng };
      const density = z.drivers_online + z.couriers_online;
      const fillOpacity = z.coverage === 'empty' ? 0.35 : Math.min(0.7, 0.15 + density * 0.08);
      const strokeWeight = z.id === selectedId ? 4 : 1.5;
      let rect = rectsRef.current[z.id];
      if (!rect) {
        rect = new g.maps.Rectangle({
          bounds, map: mapRef.current,
          strokeColor: z.color, strokeOpacity: 0.95, strokeWeight,
          fillColor: z.coverage === 'empty' ? '#ef4444' : z.color, fillOpacity,
          clickable: true,
        });
        rect.addListener('click', () => onSelect?.(z));
        rectsRef.current[z.id] = rect;
      } else {
        rect.setOptions({ bounds, strokeWeight, fillColor: z.coverage === 'empty' ? '#ef4444' : z.color, fillOpacity });
      }
    });
  }, [zones, provider, selectedId, onSelect]);

  const fallbackZones = useMemo(() => zones.slice(0, 12), [zones]);

  return (
    <div className="relative h-[70vh] w-full">
      <div ref={containerRef} className="absolute inset-0" />
      {provider === 'loading' && (
        <div className="absolute inset-0 bg-slate-50 flex items-center justify-center text-slate-400 text-sm">Loading map…</div>
      )}
      {provider === 'fallback' && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 overflow-auto">
          <div className="text-xs text-slate-500 mb-3">Add a Google Maps API key in <span className="font-mono">/admin/settings</span> to see the geographic heatmap. Coverage tiles shown below.</div>
          <div className="grid grid-cols-3 gap-2">
            {fallbackZones.map((z) => {
              const style = COVERAGE_STYLES[z.coverage] || COVERAGE_STYLES.empty;
              const density = z.drivers_online + z.couriers_online;
              return (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => onSelect?.(z)}
                  className="rounded-xl p-3 text-left transition hover:-translate-y-0.5 hover:shadow"
                  style={{ backgroundColor: `${z.color}22`, borderLeft: `4px solid ${z.color}` }}
                >
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{z.name}</div>
                  <div className={`text-[11px] font-semibold ${style.text}`}>{style.label} · {density} online</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeatmapPage;
